/**
 * The title / briefing screen — the player's first impression and the "Welcome, Counsel" framing.
 * Lays out the FULL deal background (content/briefing.ts — the cast, the security package, the role,
 * the timeline), the four performance measures, and the clock, then offers timed play or an untimed
 * practice run (WCAG 2.2.1). Editorial Borenius styling: serif headline, the red rule, restraint.
 */
import { useState } from "react";
import logoRed from "../assets/brand/logo-borenius-red.svg";
import type { Dimension } from "@patc/content-schema";
import { DEAL_LEDE } from "../content/briefing";
import { BriefingSections } from "./Briefing";

export function Brief({
  dimensions,
  cutoffLabel,
  timeLimitMinutes,
  onStart,
}: {
  dimensions: Dimension[];
  cutoffLabel: string;
  timeLimitMinutes: number;
  onStart: (mode: "timed" | "untimed") => void;
}) {
  // Stage the full background behind a reveal so the title screen stays light (mirrors the mid-game
  // "Proceed" step); the same brief is also reachable any time via the in-game "Background" toggle.
  const [showBrief, setShowBrief] = useState(false);
  return (
    <main className="mx-auto flex min-h-svh max-w-3xl flex-col px-5 py-12 sm:px-6">
      <img src={logoRed} alt="Borenius" className="mb-10 h-7 w-auto self-start" />

      <p className="t-overline text-brand">Acquisition finance · Counsel to the lenders</p>
      <span className="b-rule my-4" />
      <h1 className="font-display text-fluid-2xl font-semibold leading-tight text-ink">Panic at the Closing!</h1>

      <p className="reading-measure mt-6 text-fluid-lg leading-relaxed text-ink-700">{DEAL_LEDE}</p>

      {!showBrief ? (
        <button
          type="button"
          onClick={() => setShowBrief(true)}
          className="mt-7 inline-flex items-center gap-2 self-start rounded-sm border border-ink-300 px-5 py-2.5 font-medium text-ink transition-colors hover:border-ink"
        >
          Read the full deal background <span aria-hidden="true">→</span>
        </button>
      ) : (
        <div className="mt-8">
          <BriefingSections paneled />
        </div>
      )}

      <section className="mt-10">
        <h2 className="t-overline mb-2 text-brand">How you are judged</h2>
        <p className="reading-measure text-fluid-base leading-relaxed text-ink-700">
          Each decision shapes four measures of how you are doing. They start mid-scale; only a flawless
          run carries every one to 100. Not every problem is equally urgent — knowing what to prioritise
          is part of the job.
        </p>
        <dl className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {dimensions.map((d) => (
            <div key={d.id} className="rounded-sm border border-ink-150 bg-card px-4 py-3">
              <dt className="font-medium text-ink">{d.label}</dt>
              <dd className="mt-0.5 text-sm text-ink-600">{describe(d.id)}</dd>
            </div>
          ))}
        </dl>
      </section>

      <p className="reading-measure mt-9 text-sm text-ink-600">
        The Bank's payment cut-off is <span className="font-medium text-ink">{cutoffLabel}</span>. You have{" "}
        {timeLimitMinutes} minutes on the clock — or play untimed to learn the full spread of consequences.
      </p>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <button
          type="button"
          onClick={() => onStart("timed")}
          className="inline-flex items-center justify-center gap-2 rounded-sm bg-brand px-6 py-3 font-medium text-white transition-colors hover:bg-brand-hover"
        >
          Begin — {timeLimitMinutes} minutes on the clock <span aria-hidden="true">→</span>
        </button>
        <button
          type="button"
          onClick={() => onStart("untimed")}
          className="inline-flex items-center justify-center rounded-sm border border-ink-300 px-6 py-3 font-medium text-ink transition-colors hover:border-ink"
        >
          Untimed practice
        </button>
      </div>
    </main>
  );
}

function describe(id: Dimension["id"]): string {
  switch (id) {
    case "dealSecurity":
      return "Will signing and closing happen on schedule?";
    case "clientSatisfaction":
      return "The Bank's confidence in your judgement.";
    case "counterpartyCooperation":
      return "Goodwill of the Sponsor, the existing lender and others.";
    case "transactionProcess":
      return "How well you coordinate and pre-empt problems.";
  }
}
